import { combineEpics } from "redux-observable";
import { from, merge as mergeStatic } from "rxjs";
import {
    catchError,
    delay,
    distinctUntilChanged,
    filter,
    first,
    ignoreElements,
    map,
    mergeMap,
    tap,
} from "rxjs/operators";

import { Action, actionCreators, isOfAction } from "../actions";
import { getNewForumModalVisible } from "../reducers/createForumModal";
import { getFlattenRoomViews, getOpenRoomIds, getSetRoomToOpen, RoomType } from "../reducers/rooms";
import { notUndefined } from "../utils/any";
import { changedEvent } from "../utils/any/rxjs";
import { max_ts } from "../utils/proto";
import { Epic } from "../utils/types";

import { roomsRealtimeEpic } from "./roomsRealtime";
import { roomsSearchEpic } from "./roomsSearch";
import { roomsToggleEpic } from "./roomsToggle";
import { uploadFilesEpic } from "./uploadFile";

const onStart: Epic = (action$, state$) => {
    return action$.pipe(
        filter(isOfAction(actionCreators.onOnline)),
        first(),
        mergeMap(() => getFlattenRoomViews(state$.value).toArray()),
        map(roomView => {
            const { searchQuery, historyAroundTs } = roomView;
            if (roomView.type === RoomType.ROOM_SEARCH && searchQuery) {
                return actionCreators.roomSearchRequested({ roomView, searchQuery });
            } else if (roomView.type === RoomType.GLOBAL_SEARCH && searchQuery) {
                return actionCreators.roomSearchRequested({ roomView, searchQuery });
            } else if (roomView.type === RoomType.ACTIVITY_LOG) {
                return actionCreators.loadRoomHistoryRequested({
                    roomView,
                    how: "lt",
                    ts: max_ts,
                });
            } else if (historyAroundTs) {
                return actionCreators.loadRoomHistoryRequested({
                    roomView,
                    how: "around",
                    ts: historyAroundTs,
                });
            }
            return undefined;
        }),
        filter(notUndefined)
    );
};

const roomViewsToOpen: Epic = (action$, state$, { App }) => {
    return mergeStatic(
        state$.pipe(
            map(getNewForumModalVisible),
            distinctUntilChanged()
        ),
        changedEvent(App.mediaViewerStore),
        action$.pipe(filter(isOfAction(actionCreators.setRoomToOpen)))
    ).pipe(
        delay(1), // FIXME
        mergeMap(() => {
            const newForumModalVisible = getNewForumModalVisible(state$.value);
            const { mediaViewerVisible } = App.mediaViewerStore;
            const roomToOpen = getSetRoomToOpen(state$.value);
            if (roomToOpen && !mediaViewerVisible && !newForumModalVisible) {
                return [actionCreators.openRoomRequested({ roomView: roomToOpen })];
            }
            return [];
        })
    );
};

const join: Epic = action$ =>
    action$.pipe(
        filter(isOfAction(actionCreators.openRoomSucceeded)),
        filter(x => x.payload.roomView.type === RoomType.REAL_TIME),
        map(x => actionCreators.JoinRoom(x.payload.roomView.roomId))
    );

const leave: Epic = (action$, state$, { App }) =>
    action$.pipe(
        filter(isOfAction(actionCreators.closeRoomSucceeded)),
        tap(x =>
            x.payload.forEach(roomView => {
                if (roomView.editMode) {
                    App.actionCreators.RoomInput(roomView.roomId, "");
                }
            })
        ),
        tap(x =>
            x.payload
                .filter(x => x.type === RoomType.REAL_TIME)
                .forEach(x => App.actionCreators.LeaveRoom(x.roomId))
        ),
        ignoreElements()
    );

const onActivateOrOpenRoom: Epic = (action$, state$, { App }) => {
    return action$.pipe(
        filter(isOfAction(actionCreators.activateOrOpenRoom)),
        mergeMap(({ payload: { roomId: id } }) => {
            const newForumModalVisible = getNewForumModalVisible(state$.value);
            const { mediaViewerVisible } = App.mediaViewerStore;
            const actions: Action[] = [];
            if (newForumModalVisible) {
                actions.push(actionCreators.ToggleNewForumModalVisible());
            }

            if (mediaViewerVisible) {
                actions.push(actionCreators.ToggleMediaViewerVisible());
            }

            const isOpen = getOpenRoomIds(state$.value).has(id);
            if (!isOpen) {
                actions.push(
                    actionCreators.setRoomToOpen({
                        roomId: id,
                        type: RoomType.REAL_TIME,
                    })
                );
            } else {
                const roomView = getFlattenRoomViews(state$.value).find(
                    x => x.type === RoomType.REAL_TIME && x.roomId === id
                );
                if (roomView) {
                    actions.push(actionCreators.setRoomViewAsActive({ roomView, focus: true }));
                }
            }
            return actions;
        })
    );
};

const onActivateOrOpenRoomByUserId: Epic = (action$, state$, { App }) => {
    return action$.pipe(
        filter(isOfAction(actionCreators.activateOrOpenRoomByUserId)),
        mergeMap(({ payload: { userId } }) => {
            return from(App.proto.getRoster({ user_ids: [userId] }, { tag: "[user_ids]" })).pipe(
                mergeMap(data => {
                    const room =
                        data.rooms &&
                        data.rooms.find(
                            x =>
                                (x.dialog && x.dialog.id === userId) ||
                                (x.monolog && x.monolog.id === userId)
                        );
                    if (room) {
                        return [actionCreators.activateOrOpenRoom({ roomId: room.id })];
                    }
                    return [];
                }),
                catchError(error => {
                    console.error("Failed to get user by id", userId, error);
                    return [];
                })
            );
        })
    );
};

const onActivateOrOpenRoomView: Epic = (action$, state$, { App }) =>
    action$.pipe(
        filter(isOfAction(actionCreators.activateOrOpenRoomView)),
        map(({ payload: { roomId, type } }) => {
            const roomView = getFlattenRoomViews(state$.value).find(
                x => x.roomId === roomId && x.type === type
            );
            if (roomView) {
                return actionCreators.setRoomViewAsActive({ roomView, focus: true });
            }
            return actionCreators.setRoomToOpen({ roomId, type });
        })
    );

export const roomsEpic = combineEpics(
    onStart,
    roomViewsToOpen,
    join,
    leave,
    roomsRealtimeEpic,
    roomsSearchEpic,
    roomsToggleEpic,
    onActivateOrOpenRoom,
    onActivateOrOpenRoomByUserId,
    onActivateOrOpenRoomView,
    uploadFilesEpic
);
